import { srpFilterInitialValues } from 'redux-implementations/srpFilters';
import { FilterPill, FilterValues, SrpFilterName, SliderBlockValue } from 'types/srpFilters';
import useCheckboxFilterOptionsPillsCreators from 'components/Srp/FilterPills/hooks/useCheckboxFilterOptionsPillsCreators';
import useMultiMakeModelPillsCreator from 'components/Srp/FilterPills/hooks/useMultiMakeModelFilterPillsCreators';
import useMakeModelFilterPillsCreators from 'components/Srp/FilterPills/hooks/useMakeModelFilterPillsCreators';
import useColorFilterOptionsPillsCreators from 'components/Srp/FilterPills/hooks/useColorFilterOptionsPillsCreators';
import useCategoryFilterOptionsPillsCreators from 'components/Srp/FilterPills/hooks/useCategoryFilterOptionsPillsCreators';
import useTruckOptionsPillsCreators from 'components/Srp/FilterPills/hooks/useTruckOptionsPillsCreators';
import { useTranslation } from 'react-i18next';

export type FilterPillFn = (value?: any) => FilterPill[];

const emptyHandlers = {
  Page: (): FilterPill[] => [],
  Limit: (): FilterPill[] => [],
  SortBy: (): FilterPill[] => [],
  Location: (): FilterPill[] => [],
};

function useFuelEfficiencyPillCreator(): FilterPillFn {
  const { t } = useTranslation();

  return (value: number): FilterPill[] => {
    return [
      {
        name: SrpFilterName.FuelEfficiency,
        label: t('srp.pills.minMpg', { value }),
        resetState: {
          [SrpFilterName.FuelEfficiency]: srpFilterInitialValues[SrpFilterName.FuelEfficiency],
        },
      },
    ];
  };
}

function useTrimsPillsCreator(): FilterPillFn {
  return (trimFilters: string[]): FilterPill[] => {
    return trimFilters.map((trim) => ({
      name: trim,
      label: trim,
      resetState: { Trims: trimFilters.filter((trimFilter: string) => trimFilter !== trim) },
    }));
  };
}

function useRadiusPillCreator(): FilterPillFn {
  const { t } = useTranslation();

  return (value: number): FilterPill[] => [
    {
      name: 'radius',
      label: t('srp.pills.milesAway', { value }),
      resetState: { Radius: 50 },
    },
  ];
}

function usePricePillCreator(): FilterPillFn {
  return (filter: SliderBlockValue): FilterPill[] => {
    const { min, max } = filter;

    const displayMin = min ?? srpFilterInitialValues[SrpFilterName.Price].min;
    const displayMax = max ?? srpFilterInitialValues[SrpFilterName.Price].max;
    const priceString = `$${Number(displayMin).toLocaleString()}-$${displayMax.toLocaleString()}${
      displayMax === 100000 ? '+' : ''
    }`;

    return [
      {
        name: SrpFilterName.Price,
        label: priceString,
        resetState: { [SrpFilterName.Price]: srpFilterInitialValues[SrpFilterName.Price] },
      },
    ];
  };
}

function useMileagePillCreator(): FilterPillFn {
  const { t } = useTranslation();

  return (filter: SliderBlockValue): FilterPill[] => {
    const { max } = filter;

    return [
      {
        name: SrpFilterName.Mileage,
        label: `${t('srp.pills.mileage', { max: max.toLocaleString() })}`,
        resetState: { [SrpFilterName.Mileage]: srpFilterInitialValues[SrpFilterName.Mileage] },
      },
    ];
  };
}

function useYearPillCreator(): FilterPillFn {
  return (filter: SliderBlockValue): FilterPill[] => {
    const { min, max } = filter;

    const displayMin = min ?? srpFilterInitialValues[SrpFilterName.Year].min;
    const displayMax = max ?? srpFilterInitialValues[SrpFilterName.Year].max;
    const label = `${displayMin}-${displayMax}`;
    return [
      {
        name: SrpFilterName.Year,
        label,
        resetState: {
          [SrpFilterName.Year]: srpFilterInitialValues[SrpFilterName.Year] as FilterValues,
        },
      },
    ];
  };
}

export default function useFilterPillCreators(): Record<string, FilterPillFn> {
  const checkboxPillCreators = {
    Condition: useCheckboxFilterOptionsPillsCreators('Condition'),
    Cylinders: useCheckboxFilterOptionsPillsCreators('Cylinders', 'srp.pills.cylinders'),
    Doors: useCheckboxFilterOptionsPillsCreators('Doors', 'srp.pills.doors'),
    Advanced: useCheckboxFilterOptionsPillsCreators('Advanced'),
    BodyTypes: useCheckboxFilterOptionsPillsCreators('BodyTypes'),
    Drivetrain: useCheckboxFilterOptionsPillsCreators('Drivetrain'),
    Powertrain: useCheckboxFilterOptionsPillsCreators('Powertrain'),
    FeaturesTechnology: useCheckboxFilterOptionsPillsCreators('FeaturesTechnology'),
    Transmission: useCheckboxFilterOptionsPillsCreators('Transmission'),
  };

  return {
    ...emptyHandlers,
    ...checkboxPillCreators,

    MakeModel: useMakeModelFilterPillsCreators(),

    MultiMakeModel: useMultiMakeModelPillsCreator(),

    Trims: useTrimsPillsCreator(),

    Year: useYearPillCreator(),

    Radius: useRadiusPillCreator(),

    Price: usePricePillCreator(),

    Colors: useColorFilterOptionsPillsCreators(),

    FuelEfficiency: useFuelEfficiencyPillCreator(),

    Mileage: useMileagePillCreator(),

    TruckOptions: useTruckOptionsPillsCreators(),

    Category: useCategoryFilterOptionsPillsCreators(),
  };
}
